import type { GalleryItem } from "../components/common/PhotosDocumentsPanel";
import {
  serialiseRecordMedia,
  uploadRecordFiles,
  uploadRecordPicture,
  type MediaCarrier,
  type RecordStore,
  type RecordMedia,
} from "../utils/recordMedia";

type MediaRecord = MediaCarrier & {
  profilePicUrl?: string | null;
  medias?: RecordMedia[];
  coverUrl?: string;
};

/**
 * The picture, cover and files of a record whose media rides on the record
 * itself (parties, business units, departments — see utils/recordMedia).
 *
 * `save` persists the whole updated record and refreshes the page's copy of
 * it. What comes back plugs straight into EntityHeader's hero and the
 * PhotosDocumentsPanel, so a detail page adds media support without any
 * upload code of its own.
 */
export function useRecordMedia<T extends MediaRecord>(
  record: T | null | undefined,
  save: (updated: T) => Promise<unknown>,
  /** Where this record type's files are stored (business units, departments). */
  store?: RecordStore,
) {
  const medias: RecordMedia[] = record?.medias ?? [];

  const saveMedias = async (next: RecordMedia[]) => {
    if (!record) return;
    await save({
      ...record,
      medias: next,
      mediaJson: serialiseRecordMedia(next),
      coverUrl: next.find((m) => m.isCover)?.url || undefined,
    });
  };

  return {
    picture: record?.profilePicUrl || null,
    cover: record?.coverUrl,

    /** For EntityHeader's onImageUpload: returns the new picture's link. */
    uploadPicture: async (file: File) => {
      if (!record) throw new Error("No record");
      // The picture control shows its own progress; no full-screen loader.
      const profilePic = await uploadRecordPicture(file, record.groupId, store);
      const profilePicUrl = URL.createObjectURL(file);
      await save({ ...record, profilePic, profilePicUrl });
      return profilePicUrl;
    },

    removePicture: async () => {
      if (record) await save({ ...record, profilePic: "", profilePicUrl: "" });
    },

    /** Props for <PhotosDocumentsPanel />, minus `canEdit`. */
    panel: {
      items: medias.map((m): GalleryItem => ({ ...m, url: m.url ?? "" })),
      onAdd: async (files: File[]) => {
        if (!record) return;
        // The panel shows each upload's progress; no full-screen loader.
        const added = await uploadRecordFiles(
          files,
          record.groupId ?? "",
          store,
        );
        await saveMedias([...medias, ...added]);
      },
      onDelete: (file: GalleryItem) =>
        saveMedias(medias.filter((m) => m.id !== file.id)),
      onRename: (file: GalleryItem, name: string) =>
        saveMedias(medias.map((m) => (m.id === file.id ? { ...m, name } : m))),
      onSetCover: (file: GalleryItem | null) =>
        saveMedias(medias.map((m) => ({ ...m, isCover: m.id === file?.id }))),
    },
  };
}
